import { Cascader } from 'antd';
import type { DefaultOptionType } from 'antd/es/cascader';
import { useEffect, useState } from 'react';

type DataItem = {
  id: number;
  name: string;
  parentId?: number;
};

type Option = {
  value: number;
  label: string;
  children?: Option[];
};

const buildChildrenOptions = (
  dataSource: DataItem[],
  options: DataItem[]
): Option[] =>
  options.map((option) => {
    const children = dataSource.filter((el) => el.parentId === option.id);

    if (!children.length) {
      return {
        value: option.id,
        label: option.name,
      };
    }

    return {
      value: option.id,
      label: option.name,
      children: buildChildrenOptions(dataSource, children),
    };
  });

const mapDataSource = (dataSource: DataItem[]): Option[] => {
  const parentOptions = dataSource.filter((el) => el.parentId === undefined);
  const allOptions = buildChildrenOptions(dataSource, parentOptions);

  return allOptions;
};

const findPath = (
  leafValue: number,
  options: Option[],
  valuePath: number[]
): number[] | null =>
  options.reduce<number[] | null>((acc, option) => {
    if (acc) {
      return acc;
    }

    const newPath = [...valuePath, option.value];

    if (option.value === leafValue) {
      return newPath;
    }

    if (option.children) {
      return findPath(leafValue, option.children, newPath);
    }

    return null;
  }, null);

const getPath = (value: number | undefined, options: Option[]): number[] => {
  if (value === undefined) {
    return [];
  }

  return findPath(value, options, []) ?? [];
};

export const Cascada = ({ dataSource, value, onChange, mode }: Props) => {
  const [options, setOptions] = useState<Option[]>([]);
  const [path, setPath] = useState<number[]>([]);

  useEffect(() => {
    const newOptions = mapDataSource(dataSource);

    setOptions(newOptions);
  }, [dataSource]);

  useEffect(() => {
    const newPath = getPath(value, options);

    setPath(newPath);
  }, [options, value]);

  if (!onChange) {
    return null;
  }

  const handleChange = (selectedPath: (string | number)[]) => {
    const newValue = selectedPath?.[selectedPath.length - 1];

    return onChange(newValue as number);
  };

  const filterFunction = (
    inputValue: string,
    inputPath: DefaultOptionType[]
  ) => {
    const formattedInput = inputValue.toLowerCase();

    return inputPath.some((option) => {
      const formattedLabel = (option.label as string).toLowerCase();

      return formattedLabel.includes(formattedInput);
    });
  };

  if (mode === 'view') {
    const viewValues = path.reduce((acc: string, val: number) => {
      const name = dataSource.find((opt) => opt.id === val)?.name;

      if (acc === '') {
        return `${name}`;
      }

      return `${acc} / ${name}`;
    }, '');

    return <div>{viewValues}</div>;
  }

  return (
    <Cascader<Option>
      placeholder="Select a category"
      value={path}
      onChange={handleChange}
      expandTrigger="hover"
      options={options}
      showSearch={{ filter: filterFunction }}
      getPopupContainer={(trigger) => trigger.parentElement || trigger}
    />
  );
};

type Props = {
  dataSource: {
    id: number;
    name: string;
    parentId?: number;
  }[];
  value?: number;
  onChange?: (newValue: number) => void;
  mode: string;
};
